import * as React from "react"

import { cn } from "@components/ui/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

export const rowVariants = cva("tw:border-b", {
  variants: {
    variant: {
      default: "tw:even:bg-muted/50 tw:border-gray-200",
      contrast:
        "tw:border-gray-300 tw:even:bg-muted tw:hover:bg-primary/30 tw:border-gray-300 tw:*:border-border tw:[&>:not(:last-child)]:border-r tw:[&>:not(:last-child)]:border-r-2",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

const headerVariants = cva("tw:bg-muted tw:break-words tw:whitespace-normal", {
  variants: {
    variant: {
      default: "",
      contrast:
        "tw:border-r tw:border-r-2 tw:border-b-2 tw:border-gray-300 tw:last:border-r-0 tw:hover-primary",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="tw:relative tw:w-full tw:overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("tw:w-full tw:caption-bottom tw:text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("tw:[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("tw:[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "tw:border-t tw:bg-muted/50 tw:font-medium tw:[&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  )
}

function TableRow({
  className,
  variant,
  ...props
}: React.ComponentProps<"tr"> & VariantProps<typeof rowVariants>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "tw:border-b tw:transition-colors tw:hover:bg-muted/50 tw:data-[state=selected]:bg-muted",
        className,
        rowVariants({ variant }),
      )}
      {...props}
    />
  )
}

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"th"> & VariantProps<typeof headerVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <th
      ref={ref}
      data-slot="table-head"
      className={cn(
        "tw:relative tw:h-10 tw:px-2 tw:text-left tw:align-middle tw:font-medium tw:whitespace-nowrap tw:text-foreground tw:[&:has([role=checkbox])]:pr-0 tw:[&>[role=checkbox]]:translate-y-[2px]",
        className,
        headerVariants({ variant }),
      )}
      {...props}
    />
  )
})
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"td">
>(({ className, ...props }, ref) => {
  return (
    <td
      ref={ref}
      data-slot="table-cell"
      className={cn(
        "tw:p-2 tw:align-middle tw:whitespace-nowrap tw:[&:has([role=checkbox])]:pr-0 tw:[&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  )
})
TableCell.displayName = "TableCell"

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("tw:mt-4 tw:text-sm tw:text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
